import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { showUpgradeExternally } from "./UpgradePrompt";
import { toast } from "sonner";

// Anything on the site can open the box with: window.dispatchEvent(new Event(OPEN_SUGGESTION))
export const OPEN_SUGGESTION = "dtm-open-suggestion";
export const openSuggestionBox = () => window.dispatchEvent(new Event(OPEN_SUGGESTION));

// Shown to a guest when Dan has switched on "guests must create a free login" (Admin, Feedback).
const inviteToSignUp = () => showUpgradeExternally("Create a free login to send a suggestion", {
  ctaLabel: "Create a free login",
  ctaPath: "/register",
  body: "To send a suggestion, please create a free login first. It keeps robots out so Dan can read every real one, and it only takes a moment.",
});

const TOPICS = [["idea", "An idea"], ["problem", "Something's broken"], ["other", "Other"]];

// "Send Dan a suggestion". Works for members and for guests. Mounted once, in components/Layout.js.
export default function SuggestionBox() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [topic, setTopic] = useState("idea");
  const [message, setMessage] = useState("");
  const [reply, setReply] = useState(false);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    const show = () => {
      setTopic("idea"); setMessage(""); setReply(false); setEmail(""); setSent(false); setBusy(false); setOpen(true);
    };
    window.addEventListener(OPEN_SUGGESTION, show);
    return () => window.removeEventListener(OPEN_SUGGESTION, show);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  if (!open) return null;

  const send = async () => {
    if (message.trim().length < 3) { toast.error("Please type your suggestion first."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/feedback", { topic, message: message.trim(), can_reply: reply, email: reply && !user ? email.trim() : "", page: window.location.pathname });
      // A guest is asked to create a login here, and is not told whether the message was delivered.
      if (data && data.ask_login) { setOpen(false); inviteToSignUp(); } else setSent(true);
    } catch (e) {
      if (e.response?.data?.detail === "login_required") { setOpen(false); inviteToSignUp(); }
      else toast.error(e.response?.data?.detail || "Could not send. Please try again.");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="suggestion-box" role="dialog" aria-modal="true" aria-labelledby="suggestion-title"
      className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-3" onClick={() => setOpen(false)}>
      <div className="w-full max-w-md max-h-[92vh] overflow-y-auto bg-zinc-900 border border-white/10 rounded-2xl p-5 sm:p-6" onClick={(e) => e.stopPropagation()}>
        {sent ? (
          <div data-testid="suggestion-thanks">
            <h2 id="suggestion-title" className="font-display text-2xl font-bold">Thank you!</h2>
            <p className="text-sm text-zinc-300 mt-2">Dan got it.</p>
            <button type="button" onClick={() => setOpen(false)} data-testid="suggestion-close" className="mt-4 px-5 py-2.5 rounded-full bg-cyan-400 text-black font-semibold hover:bg-cyan-300">Close</button>
          </div>
        ) : (
          <>
            <h2 id="suggestion-title" className="font-display text-2xl font-bold">Send Dan a suggestion</h2>
            <p className="text-sm text-zinc-400 mt-1 mb-3">A song you want, something that's broken, or an idea. Dan reads every one.</p>
            <div className="flex flex-wrap gap-2 mb-3" role="radiogroup" aria-label="What kind of message is it?">
              {TOPICS.map(([k, label]) => (
                <button key={k} type="button" role="radio" aria-checked={topic === k} onClick={() => setTopic(k)} data-testid={`suggestion-topic-${k}`}
                  className={`px-3 py-1.5 rounded-full border text-sm ${topic === k ? "border-cyan-400/60 bg-cyan-400/10 text-cyan-200" : "border-white/15 text-zinc-300 hover:bg-white/5"}`}>{label}</button>
              ))}
            </div>
            <textarea value={message} onChange={(e) => setMessage(e.target.value)} maxLength={2000} rows={5} autoFocus aria-label="Your suggestion"
              placeholder={topic === "problem" ? "What happened? What were you doing, and what did you see?" : "Type your suggestion here"} data-testid="suggestion-text"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-base sm:text-sm focus:outline-none focus:border-cyan-400/50" />
            <label className="mt-3 flex items-start gap-3 text-sm cursor-pointer">
              <input type="checkbox" checked={reply} onChange={(e) => setReply(e.target.checked)} data-testid="suggestion-reply" className="w-4 h-4 mt-0.5 accent-cyan-400" />
              <span>{user ? <>Dan can reply to me by email (<span className="break-all">{user.email}</span>)</> : "Dan can reply to me by email"}</span>
            </label>
            {reply && !user && (
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Your email address" aria-label="Your email address" data-testid="suggestion-email"
                className="mt-2 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-base sm:text-sm focus:outline-none focus:border-cyan-400/50" />
            )}
            <p className="text-xs text-zinc-500 mt-3">Dan uses this to fix and improve the site. We also keep which phone or computer and browser you used, to help track down problems. Never sold or shared for ads. <Link to="/privacy" className="text-cyan-400 hover:underline" onClick={() => setOpen(false)}>Privacy</Link></p>
            <div className="mt-4 flex flex-wrap gap-2">
              <button type="button" disabled={busy} onClick={send} data-testid="suggestion-send" className="px-5 py-2.5 rounded-full bg-cyan-400 text-black font-semibold hover:bg-cyan-300 disabled:opacity-60">{busy ? "Sending..." : "Send"}</button>
              <button type="button" disabled={busy} onClick={() => setOpen(false)} data-testid="suggestion-cancel" className="px-5 py-2.5 rounded-full border border-white/20 hover:bg-white/5">Cancel</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
